import { useCallback, useEffect, useState } from "react";
import { config } from "../config";
import { erc20Abi, marketAbi } from "../abi";
import { l2Public } from "../lib/chain";
import { fetchPositions, type Position } from "../lib/explore";
import { approveIfNeeded, writeL2 } from "../lib/tx";
import { fmtToken, parseToken, shortAddr } from "../lib/format";
import { stashStakePrefill } from "./Brains";
import { useWallet } from "../wallet";
import { ActionButton, Card, Field, Notice, Stat } from "../components/ui";

export function Account() {
  const { account } = useWallet();
  const [walletBal, setWalletBal] = useState<bigint | null>(null);
  const [spending, setSpending] = useState<bigint | null>(null);
  const [pending, setPending] = useState<{ amount: bigint; readyAt: number } | null>(null);
  const [positions, setPositions] = useState<Position[] | null>(null);
  const [depositAmt, setDepositAmt] = useState("1");
  const [withdrawAmt, setWithdrawAmt] = useState("1");
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!account) return;
    const c = config.l2.contracts;
    l2Public
      .readContract({ address: c.based, abi: erc20Abi, functionName: "balanceOf", args: [account] })
      .then((b) => setWalletBal(b as bigint))
      .catch(() => setWalletBal(null));
    l2Public
      .readContract({ address: c.market, abi: marketAbi, functionName: "balances", args: [account] })
      .then((b) => setSpending(b as bigint))
      .catch(() => setSpending(null));
    l2Public
      .readContract({ address: c.market, abi: marketAbi, functionName: "pendingWithdrawals", args: [account] })
      .then((p) => {
        const [amount, readyAt] = p as [bigint, bigint];
        setPending(amount > 0n ? { amount, readyAt: Number(readyAt) } : null);
      })
      .catch(() => setPending(null));
    fetchPositions(account).then(setPositions);
  }, [account, nonce]);

  if (!account) return <ConnectPrompt />;

  const now = Math.floor(Date.now() / 1000);
  const withdrawReady = pending !== null && now >= pending.readyAt;

  return (
    <>
      <div className="section-title">Spending account</div>
      <div className="grid cols-3">
        <Card>
          <Stat label="Wallet $basedAI" value={walletBal !== null ? fmtToken(walletBal) : "—"} unit="BASED" />
        </Card>
        <Card>
          <Stat label="Spending balance" value={spending !== null ? fmtToken(spending) : "—"} unit="BASED" />
        </Card>
        <Card>
          <Stat
            label="Pending withdrawal"
            value={pending ? fmtToken(pending.amount) : "None"}
            unit={pending ? "BASED" : undefined}
          />
        </Card>
      </div>

      <div className="grid cols-2" style={{ marginTop: 18 }}>
        <Card title="Deposit" sub="Fund your spending balance to pay for inference.">
          <Field label="Amount ($basedAI)">
            <input className="input" value={depositAmt} onChange={(e) => setDepositAmt(e.target.value)} />
          </Field>
          <ActionButton
            onRun={async () => {
              const amt = parseToken(depositAmt);
              await approveIfNeeded(account, config.l2.contracts.based, config.l2.contracts.market, amt);
              return writeL2(account, { address: config.l2.contracts.market, abi: marketAbi, functionName: "deposit", args: [amt] });
            }}
            onDone={refresh}
          >
            Approve &amp; deposit
          </ActionButton>
        </Card>

        <Card title="Withdraw" sub="Two steps: request, then withdraw after a 1-day safety delay.">
          <Field
            label="Amount ($basedAI)"
            hint="Miners can still redeem signed receipts during the delay; they take priority over your exit."
          >
            <input className="input" value={withdrawAmt} onChange={(e) => setWithdrawAmt(e.target.value)} />
          </Field>
          <div className="row">
            <ActionButton
              variant="ghost"
              onRun={() =>
                writeL2(account, {
                  address: config.l2.contracts.market,
                  abi: marketAbi,
                  functionName: "requestWithdraw",
                  args: [parseToken(withdrawAmt)],
                })
              }
              onDone={refresh}
            >
              Request
            </ActionButton>
            <ActionButton
              onRun={() => writeL2(account, { address: config.l2.contracts.market, abi: marketAbi, functionName: "withdraw" })}
              onDone={refresh}
              disabled={!withdrawReady}
            >
              Withdraw
            </ActionButton>
          </div>
          {pending && !withdrawReady && (
            <div style={{ marginTop: 12 }}>
              <Notice kind="warn">
                Withdrawal ready {new Date(pending.readyAt * 1000).toLocaleString()}.
              </Notice>
            </div>
          )}
        </Card>
      </div>

      <div className="section-title">Your stake positions</div>
      <Card>
        {positions === null ? (
          <div className="muted">Loading…</div>
        ) : positions.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            No stake positions yet. Browse the <a href="#/brains">Brain explorer</a> to find a validator
            worth delegating to.
          </p>
        ) : (
          <table className="doc-table">
            <thead>
              <tr>
                <th>Brain</th>
                <th>Validator</th>
                <th>Active stake</th>
                <th>In cooldown</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {positions.map((p) => (
                <tr key={`${p.brainId}:${p.validator}`}>
                  <td><a href={`#/brains/${p.brainId}`}>#{p.brainId}</a></td>
                  <td><span className="mono">{shortAddr(p.validator)}</span></td>
                  <td>{fmtToken(p.active)} BASED</td>
                  <td>{p.cooldown > 0n ? `${fmtToken(p.cooldown)} BASED` : "—"}</td>
                  <td>
                    <a className="btn ghost sm" href="#/stake" onClick={() => stashStakePrefill(p.brainId, p.validator)}>
                      Manage
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

export function ConnectPrompt() {
  const { connect, hasWallet, connecting } = useWallet();
  return (
    <div style={{ marginTop: 24 }}>
      <Card title="Connect your wallet">
        <p className="muted">Connect an injected wallet (MetaMask or compatible) to continue.</p>
        <button className="btn" onClick={() => void connect()} disabled={!hasWallet || connecting}>
          {hasWallet ? "Connect Wallet" : "No wallet detected"}
        </button>
      </Card>
    </div>
  );
}
